import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive  cursor-pointer",
  {
    variants: {
      variant: {
        default:       "bg-primary75 text-primary-foreground hover:bg-primary50 active:bg-primary",
        warning:       "border border-orange/60 text-orange bg-background hover:bg-orange/20 dark:bg-transparent dark:text-primary25 dark:hover:bg-primary50/80 dark:hover:text-white",
        outline:       "border border-border bg-background hover:bg-muted hover:text-foreground dark:text-white/70 dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        outline_prime: "border border-primary75 text-primary75 dark:text-primary25 bg-transparent hover:bg-primary50 dark:hover:text-white hover:text-primary-foreground active:bg-primary",
        secondary:     "border border-primary25 text-primary25 bg-secondary hover:bg-secondary-light dark:hover:bg-primary50/80 dark:hover:text-white",
        ghost:         "hover:bg-muted hover:text-foreground dark:hover:bg-accent/50",
        link:          "text-primary underline-offset-4 hover:underline",
        accent:        "bg-orange text-white hover:bg-orange-light active:bg-orange-lightest",
        accent2:       "bg-green text-white hover:bg-green-dark active:bg-green",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-6 px-2   py-3 text-xs",
        sm: "h-7 px-2.5 py-4 text-[0.8rem]",
        lg: "h-9 px-4   py-4",
        icon: "size-8",
        "icon-xs": "size-6",
        "icon-sm": "size-7",
        "icon-lg": "size-9",
        xl: "h-12 px-12 text-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({ className, variant = "default", size = "default", asChild = false, ...props }: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
